<script lang="ts">
/**
 * @module routes/[[locale=locale]]/projects/+page
 * @description Projects showcase page with search filtering.
 * Supports client-side search with URL query params via runed's useSearchParams.
 */

import AuroraText from "$lib/components/AuroraText.svelte";
import * as ProjectCard from "$lib/components/project-card";
import PinnedProjects from "$lib/components/PinnedProjects.svelte";
import {
  FolderOpen,
  Grid3x3Icon as Grid3X3,
  Search,
  X,
  LoaderCircle as Loader2,
} from "@lucide/svelte";
import { t, locale, type LocaleCode } from "$lib/i18n";
import { browser } from "$app/env";
import { searchProjects } from "$lib/remote/projects.remote";
import { cn } from "$lib/utils";
import { z } from "zod";
import { useSearchParams } from "runed/kit";

// Schema for URL search params — runed handles serialization, debounce, and URL sync
const searchSchema = z.object({
  q: z.string().default(""),
});

const params = useSearchParams(searchSchema, {
  debounce: 300,
  pushHistory: false,
  noScroll: true,
});

$effect(() => {
  return () => params.cleanup();
});

let inputRef: HTMLInputElement;

function clearSearch() {
  params.q = "";
  inputRef?.focus();
}

let { data } = $props();

let allCountRes = $derived(data.allProjects.length);

let searchArgs = $derived(
  browser && params.q
    ? {
        lang: $locale as LocaleCode,
        searchQuery: params.q,
        pinnedOnly: false,
      }
    : undefined,
);

let searchResData = $state<any[] | null>(null);

$effect(() => {
  if (searchArgs) {
    let active = true;
    searchProjects(searchArgs).then((res) => {
      if (active) searchResData = res;
    });
    return () => {
      active = false;
    };
  } else {
    searchResData = null;
  }
});

let projectsRes = $derived(
  searchArgs && searchResData ? searchResData : data.allProjects,
);
</script>

<svelte:head>
  <title>{$t("projects.title")} | {$t("layout.title")}</title>
</svelte:head>

<div class={cn("flex flex-col gap-12 px-6 py-10 lg:px-8 mx-auto")}>
  <section class={cn("text-center space-y-6")}>
    <AuroraText
      class={cn(
        "text-4xl md:text-5xl lg:text-6xl font-bold space-x-6 space-y-6",
      )}
      colors={["#C16069", "#A2BF8A", "#C16069", "#A2BF8A"]}
      speed={3}
    >
      {$t("projects.title")}
    </AuroraText>
    <p
      class={cn(
        "text-muted-foreground text-lg flex items-center justify-center gap-2",
      )}
    >
      <FolderOpen class={cn("w-6 h-6")} />
      {$t("projects.projectCount", {
        count: allCountRes.toString(),
      })}
    </p>
  </section>
  <section
    class={cn("max-w-96 lg:max-w-1/2 items-center justify-center mx-auto")}
  >
    <PinnedProjects />
  </section>

  <section class={cn("space-y-6")}>
    <h2
      class={cn(
        "text-3xl md:text-4xl font-bold text-center flex items-center justify-center gap-3",
      )}
    >
      <Grid3X3 class={cn("w-7 h-7 text-primary")} />
      {$t("projects.all")}:
    </h2>

    <!-- Search Input -->
    <div class="relative w-full max-w-md mx-auto">
      <Search
        class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground"
      />
      <input
        bind:this={inputRef}
        type="text"
        placeholder={String($t("projects.searchPlaceholder"))}
        class="w-full pl-10 pr-10 py-3 rounded-lg border border-border bg-background/50
               text-foreground placeholder:text-muted-foreground
               focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
               transition-all duration-200"
        bind:value={params.q}
      />
      {#if params.q}
        <button
          type="button"
          class="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          onclick={clearSearch}
          aria-label="Clear search"
        >
          <X class="w-5 h-5" />
        </button>
      {/if}
    </div>

    <!-- Active filter indicator -->
    {#if params.q}
      <p class="text-md text-center text-muted-foreground">
        {$t("projects.showingResultsFor", { query: params.q })}
      </p>
    {/if}
  </section>
  <section
    class={cn("max-w-96 lg:max-w-1/2 items-center justify-center mx-auto")}
  >
    {#if projectsRes.length > 0}
      <div class={cn("grid grid-cols-1 gap-5")}>
        {#each projectsRes as project (project.key)}
          <ProjectCard.Root
            projektImage={project.image}
            projektImageAlt={$t(`projects.items.${project.id}.imgAlt`)}
            projektImageBgColor={project.bgColor}
            projektTitle={project.translatedTitle}
            projektBadges={project.badges}
            projektLink={project.link}
          >
            {project.translatedDescription}
          </ProjectCard.Root>
        {/each}
      </div>
    {:else}
      <div class="text-center py-12">
        <p class="text-muted-foreground text-lg">
          {$t("projects.noResults")}
        </p>
        <button
          type="button"
          class="mt-4 text-primary hover:underline"
          onclick={clearSearch}
        >
          {$t("projects.clearSearch")}
        </button>
      </div>
    {/if}
  </section>
</div>
